'use client';
import React, { useState, useEffect } from 'react';
import { settingsService, type ClubSettings } from '@/lib/supabaseService';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

const DEFAULT_SETTINGS: ClubSettings = {
  clubName: 'IA Climb',
  stravaClubId: '1234567',
  stravaClubUrl: 'https://www.strava.com/clubs/iaclimb',
  autoApproveStrava: false,
  requireClubVerification: true,
  seasonName: 'Stagione 2026',
  seasonStart: '2026-03-01',
  seasonEnd: '2026-10-31',
  contactEmail: 'admin@iaclimb.cc',
};

export default function AdminSettingsTab() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<ClubSettings>({
    defaultValues: DEFAULT_SETTINGS,
  });

  useEffect(() => {
    settingsService.get().then((data) => {
      if (data) reset(data);
    }).catch(() => {}).finally(() => setLoadingSettings(false));
  }, [reset]);

  const onSubmit = async (data: ClubSettings) => {
    setSaving(true);
    try {
      await settingsService.save(data);
      setSaved(true);
      toast.success('Impostazioni salvate');
      setTimeout(() => setSaved(false), 3000);
    } catch {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fade-in max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Club info section */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="UserGroupIcon" size={16} className="text-primary" />
            Informazioni Club
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Nome club</label>
              <input type="text" className="input-field w-full px-4 py-2.5 text-sm" {...register('clubName', { required: 'Nome club obbligatorio' })} />
              {errors.clubName && <p className="mt-1 text-xs text-red-400">{errors.clubName.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">ID Club Strava</label>
                <p className="text-xs text-muted-foreground mb-1.5">Trovalo nell&apos;URL del club Strava.</p>
                <input type="text" className="input-field w-full px-4 py-2.5 text-sm font-mono-nums" {...register('stravaClubId', { required: 'ID club Strava obbligatorio' })} />
                {errors.stravaClubId && <p className="mt-1 text-xs text-red-400">{errors.stravaClubId.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email contatto</label>
                <input type="email" className="input-field w-full px-4 py-2.5 text-sm" {...register('contactEmail', { required: 'Email obbligatoria' })} />
                {errors.contactEmail && <p className="mt-1 text-xs text-red-400">{errors.contactEmail.message}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">URL Club Strava</label>
              <input type="url" className="input-field w-full px-4 py-2.5 text-sm" {...register('stravaClubUrl')} />
            </div>
          </div>
        </div>

        {/* Verification section */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ShieldCheckIcon" size={16} className="text-primary" />
            Verifica Iscrizione
          </h3>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer group">
              <div>
                <p className="text-sm font-semibold text-foreground">Verifica iscrizione club obbligatoria</p>
                <p className="text-xs text-muted-foreground mt-0.5">Gli utenti devono essere verificati prima di poter inviare tempi.</p>
              </div>
              <div className="relative ml-4">
                <input type="checkbox" className="sr-only peer" {...register('requireClubVerification')} />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary transition-colors duration-200" />
                <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
              </div>
            </label>
            <label className="flex items-center justify-between cursor-pointer group">
              <div>
                <p className="text-sm font-semibold text-foreground">Approva automaticamente tempi da Strava</p>
                <p className="text-xs text-muted-foreground mt-0.5">I tempi importati da Strava vengono approvati senza revisione manuale.</p>
              </div>
              <div className="relative ml-4">
                <input type="checkbox" className="sr-only peer" {...register('autoApproveStrava')} />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary transition-colors duration-200" />
                <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
              </div>
            </label>
          </div>
        </div>

        {/* Season section */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="CalendarDaysIcon" size={16} className="text-primary" />
            Stagione Competitiva
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Nome stagione</label>
              <input type="text" className="input-field w-full px-4 py-2.5 text-sm" {...register('seasonName', { required: 'Nome stagione obbligatorio' })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Data inizio</label>
                <input type="date" className="input-field w-full px-4 py-2.5 text-sm" {...register('seasonStart', { required: true })} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Data fine</label>
                <input type="date" className="input-field w-full px-4 py-2.5 text-sm" {...register('seasonEnd', { required: true })} />
              </div>
            </div>
          </div>
        </div>

        {/* Danger zone */}
        <div className="bg-red-400/5 border border-red-400/20 rounded-xl p-6">
          <h3 className="text-sm font-bold text-red-400 mb-4 flex items-center gap-2">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-red-400" />
            Zona Pericolosa
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Reset classifica stagione</p>
                <p className="text-xs text-muted-foreground">Elimina tutti i tempi della stagione corrente.</p>
              </div>
              <button type="button" onClick={() => toast.error('Funzione disabilitata in modalità demo')} className="px-3 py-2 rounded-lg text-xs font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors">
                Reset stagione
              </button>
            </div>
          </div>
        </div>

        {/* Save bar */}
        <div className="flex items-center justify-between py-4 border-t border-border">
          {isDirty && !saved && (
            <p className="text-xs text-yellow-400 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              Modifiche non salvate
            </p>
          )}
          {saved && (
            <p className="text-xs text-green-400 flex items-center gap-1 fade-in">
              <Icon name="CheckCircleIcon" size={14} />
              Salvato
            </p>
          )}
          {!isDirty && !saved && <span />}
          <button type="submit" disabled={saving} className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60" style={{ minWidth: '140px' }}>
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Salvataggio...
              </>
            ) : (
              <>
                <Icon name="CheckIcon" size={16} />
                Salva impostazioni
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}